import { currentProjectSlug, currentBranch } from '../utils/projectPath';

const fetchData = async (endpoint, options = {}) => {
  // PORTAL (standalone deployment): all /api traffic stays same-origin — the
  // portal nginx routes it to the IN-APP portal-api. The baked absolute
  // cortex URL must never be used there (full runtime isolation).
  const backendUrl = (typeof window !== 'undefined' && window.__PORTAL__?.portal)
    ? '/'
    : import.meta.env.VITE_BACKEND_ADDRESS;
  if (!backendUrl) {
    throw new Error('VITE_BACKEND_ADDRESS environment variable is not defined');
  }

  try {
    // Extract custom options
    const { orderBy, limit, offset, responseType, ...fetchOptions } = options;

    // Get auth token from localStorage. A share visitor signs in on the share
    // page itself and gets an ordinary cortex session — there is no second
    // token type to choose between.
    const token = localStorage.getItem('access_token');

    // Add Authorization header if token exists
    const headers = {
      ...fetchOptions.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // The URL is the single source of truth for the active project + branch.
    // Derive them from the current path and send per-request; the backend routes
    // the Dolt DB/branch from these headers (NOT from the JWT). If there is no
    // project in the URL yet (pre-redirect), we send nothing and the backend
    // falls back to its default.
    const proj = currentProjectSlug();
    if (proj && !headers['X-Cortex-Project']) headers['X-Cortex-Project'] = proj;
    if (proj && !headers['X-Cortex-Branch']) headers['X-Cortex-Branch'] = currentBranch();

    // Acting-org scope (platform admins only — the backend ignores it for
    // everyone else): the org switcher on /projects stores the selection here;
    // sending it on every call keeps all admin views consistently scoped.
    const actingOrg = localStorage.getItem('cortex_acting_org');
    if (actingOrg && !headers['X-Cortex-Org']) headers['X-Cortex-Org'] = actingOrg;

    // Build query parameters
    const queryParams = new URLSearchParams();
    if (orderBy) {
      queryParams.append('sort', orderBy);
    }
    if (limit) {
      queryParams.append('limit', limit);
    }
    if (offset !== undefined && offset !== null) {
      queryParams.append('offset', offset);
    }

    // Build URL with query parameters
    const queryString = queryParams.toString();
    const url = `${backendUrl}api/${endpoint}${queryString ? '?' + queryString : ''}`;

    // Use the backend service name and port
    const response = await fetch(url, {
      ...fetchOptions,
      headers
    });

    if (!response.ok) {
      // The parsed body IS the error (callers read .error/.message). Attach the
      // HTTP status too: distinguishing 401 (no session) from 403 (signed in,
      // not permitted) drives real behavior — the share page shows its own
      // sign-in gate for the former and "not shared with you" for the latter —
      // and body wording is too fragile to carry that decision. Non-enumerable
      // so it never lands in a JSON.stringify of the error.
      const error = await response.json().catch(() => ({
        error: response.statusText || 'Request failed',
        message: `HTTP ${response.status}`,
      }));
      if (error && typeof error === 'object') {
        Object.defineProperty(error, 'status', { value: response.status, enumerable: false });
      }
      throw error;
    }

    // Handle different response types
    if (responseType === 'arrayBuffer') {
      return await response.arrayBuffer();
    } else if (responseType === 'blob') {
      return await response.blob();
    } else if (responseType === 'text') {
      return await response.text();
    } else {
      // Default to JSON
      return await response.json();
    }
  } catch (error) {
    // Requests aborted by navigating away mid-fetch are expected; logging them
    // at error level makes every page transition look broken in the console.
    // Real network failures ("Failed to fetch" while the page stays up) still
    // matter — downgrade rather than drop, and keep rethrowing either way so
    // callers surface the failure in the UI.
    if (error?.name === "AbortError" || /Failed to fetch/i.test(String(error?.message || ""))) {
      console.warn("fetch interrupted:", endpoint, error?.message || error);
    } else {
      console.error("Error fetching data:", error);
    }
    throw error;
  }
};

export { fetchData };
